import React from "react"
import SummaryItem, {
  type SummaryItemProps,
} from "@/screens/loans/components/SummaryItem"
import { ActivityIndicator, View } from "react-native"
import { Vertical } from "@/components/standard/BFLayout"
import { H2, Tiny } from "@/components/standard/BFText"
import ToolLine from "@/components/util/ToolLine"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import {
  numberToCurrency,
  numberToPercentage,
  stringToNumber,
} from "@/utils/NumberUtils"
import Animated from "react-native-reanimated"
import useUpdateDimming from "@/hooks/useUpdateDimming"
import { useRehabBudgetingFeature } from "@/hooks/features/useRehabBudgetingFeature"
import { useIsDesktop } from "@/hooks/useIsDesktop"

interface HeadlineSummarySectionProps {
  title: string
  items: SummaryItemProps[]
  bottomLine: SummaryItemProps
  openModal: (category: string) => void
  // Optional dimming style for the gray container
  dimStyle?: any
  canEdit?: boolean
}

export const HeadlineSummarySection = ({
  title,
  items,
  bottomLine,
  openModal,
  dimStyle = { opacity: 1 },
  canEdit = true, // When budget > 0
}: HeadlineSummarySectionProps) => {
  const { loanApplication } = useLoanContext()
  const isDesktop = useIsDesktop()

  const { isEditable } = useRehabBudgetingFeature(loanApplication, isDesktop)

  const onEditContingency = () => {
    openModal("contingency")
  }

  return (
    <Vertical>
      <View className="flex-row items-center justify-between my-2">
        <H2 className="text-midnight-blue">{title}</H2>
      </View>
      <Animated.View style={dimStyle}>
        <Vertical className="flex flex-col bg-grey border border-light-grey rounded p-4 py-2">
          <View>
            {items.map((item, index) => (
              <SummaryItem
                key={index}
                tight={true}
                buttonText={isEditable ? "Edit" : "View"}
                onEditPress={
                  item.label.includes("Contingency") && canEdit
                    ? () => {
                        onEditContingency()
                      }
                    : undefined
                }
                {...item}
              />
            ))}
          </View>
          <ToolLine className="mb-2" />
          <View>
            <SummaryItem bolded={true} tight={false} {...bottomLine} />
          </View>
        </Vertical>
      </Animated.View>
    </Vertical>
  )
}

export const RehabSOWHeadlineSummary = ({
  openModal,
}: {
  openModal: (category: string) => void
}) => {
  const { loanApplication } = useLoanContext()
  const isDesktop = useIsDesktop()
  const {
    rehabBudget,
    isLoading,
    isPending,
    isRefetching,
    isDetailsTabDisabled,
    error,
  } = useRehabBudgetingFeature(loanApplication, isDesktop)

  // Use the updated hook by passing isRefetching
  const { animatedDimStyle } = useUpdateDimming(isRefetching)

  const subtotal: number = stringToNumber(rehabBudget?.subtotal || "0")
  const contingency: number = stringToNumber(
    rehabBudget?.contingencyValue || "0"
  )
  const contingencyAmt: number = stringToNumber(
    rehabBudget?.contingencyAmount || "0"
  )
  const total: number = subtotal + contingency
  const contingencyLabel: string =
    `Contingency (${numberToPercentage(contingencyAmt * 100, 0)})` || "0%"

  const items = [
    { label: "Subtotal", value: numberToCurrency(Math.round(subtotal), 0) },
    {
      label: contingencyLabel,
      value: numberToCurrency(Math.round(contingency), 0),
    },
  ]
  const bottomLine = {
    label: "Total Rehab SOW Budget",
    value: numberToCurrency(Math.round(total), 0),
  }

  if (isLoading || isPending) {
    return <ActivityIndicator className="flex-1 items-center" size={"small"} />
  }

  if (error) {
    return <H2>Error loading rehab budget</H2>
  }

  return (
    <>
      <HeadlineSummarySection
        title={"Rehab SOW"}
        items={items}
        bottomLine={bottomLine}
        openModal={openModal}
        canEdit={!isDetailsTabDisabled}
        // Pass the animated style to only fade the gray container on loading
        dimStyle={animatedDimStyle}
      />
      {!!loanApplication.rehabEstimate && (
        <Tiny className="mt-2">{`Your original estimated rehab budget was ${numberToCurrency(loanApplication.rehabEstimate)}`}</Tiny>
      )}
    </>
  )
}
